'use client'

import { useState } from 'react'
import { motion } from 'framer-motion'
import { RotateCcw } from 'lucide-react'
import ParisLevel from '../LoveRunGame/levels/Paris/ParisLevel'
import VeniceLevel from '../LoveRunGame/levels/Venise/VeniceLevel' // Renommé en VeniceLevel mais fichier s'appelle BeachLevel
import PSGLevel from '../LoveRunGame/levels/PSG/PSGLevel'
import WeddingLevel from '../LoveRunGame/levels/Wedding/WeddingLevel'

export interface GameLevel {
	id: number
	name: string
	scene: 'paris' | 'venise' | 'mountain' | 'psg' | 'wedding'
	description: string
	difficulty: 'easy' | 'medium' | 'hard' | 'expert'
	unlocked: boolean
	completed: boolean
	bestScore: number
}

const GAME_LEVELS: GameLevel[] = [
	{
		id: 1,
		name: "Un parisien rencontre une parisienne",
		scene: 'paris',
		description: "Évitez les cyclistes énervés dans les rues de Paris !",
		difficulty: 'easy',
		unlocked: true,
		completed: false,
		bestScore: 0
	},
	{
		id: 2,
		name: "Julia partage ses passions avec Ben",
		scene: 'venise',
		description: "Julia court après Benjamin dans les canaux de Venise !",
		difficulty: 'medium',
		unlocked: false,
		completed: false,
		bestScore: 0
	},
	{
		id: 3,
		name: "Ben partage ses passions avec Julia",
		scene: 'psg',
		description: "Ben court après Julia sur la pelouse du PSG !",
		difficulty: 'hard',
		unlocked: false,
		completed: false,
		bestScore: 0
	},
	{
		id: 4,
		name: "Le mariage",
		scene: 'wedding',
		description: "Le dernier défi avant le grand jour !",
		difficulty: 'expert',
		unlocked: false,
		completed: false,
		bestScore: 0
	}
]

const LoveRunGame = ({ onBack, onGameEnd }: { onBack: () => void; onGameEnd?: (score: number) => void }) => {
	const [currentView, setCurrentView] = useState<'playing' | 'transition' | 'completed'>('playing') // Démarrer directement en jeu
	const [selectedLevel, setSelectedLevel] = useState<GameLevel | null>(GAME_LEVELS[0]) // Commencer par le niveau 1
	const [nextLevel, setNextLevel] = useState<GameLevel | null>(null) // Niveau suivant pour la transition
	const [levels, setLevels] = useState<GameLevel[]>(GAME_LEVELS)
	const [lastCompletedScore, setLastCompletedScore] = useState<number>(0)

	const handleLevelComplete = (levelId: number, score: number) => {
		setLastCompletedScore(score)

		const updatedLevels = levels.map(level => {
			if (level.id === levelId) {
				return {
					...level,
					completed: true,
					bestScore: Math.max(level.bestScore, score)
				}
			}
			// Débloquer le niveau suivant
			if (level.id === levelId + 1) {
				return { ...level, unlocked: true }
			}
			return level
		})

		setLevels(updatedLevels)

		// Vérifier s'il y a un niveau suivant
		const foundNextLevel = GAME_LEVELS.find(level => level.id === levelId + 1)
		if (foundNextLevel) {
			setNextLevel(foundNextLevel)
			setCurrentView('transition') // Montrer l'écran de transition
		} else {
			// Si c'est le dernier niveau, calculer le score total et appeler onGameEnd
			const totalScore = updatedLevels.reduce((total, level) => total + level.bestScore, 0)
			if (onGameEnd) {
				onGameEnd(totalScore)
			}
			setCurrentView('completed')
			setSelectedLevel(null)
		}
	}

	const handleContinueToNextLevel = () => {
		if (nextLevel) {
			setSelectedLevel(nextLevel)
			setNextLevel(null)
			setCurrentView('playing')
		}
	}

	const handleBackToMenu = () => {
		// Reset au niveau 1 quand on revient au menu
		setSelectedLevel(GAME_LEVELS[0])
		setCurrentView('playing')
	}

	const renderLevel = () => {
		if (!selectedLevel) return null

		switch (selectedLevel.scene) {
			case 'paris':
				return (
					<ParisLevel
						level={selectedLevel}
						onComplete={handleLevelComplete}
						onBack={onBack}
					/>
				)
			case 'psg':
				return (
					<PSGLevel
						level={selectedLevel}
						onComplete={handleLevelComplete}
						onBack={onBack}
					/>
				)
			case 'venise':
				return (
					<VeniceLevel
						level={selectedLevel}
						onComplete={handleLevelComplete}
						onBack={onBack}
					/>
				)
			case 'wedding':
				return (
					<WeddingLevel
						level={selectedLevel}
						onComplete={handleLevelComplete}
						onBack={onBack}
					/>
				)
			default:
				return null
		}
	}

// Écran de transition entre les niveaux
	if (currentView === 'transition' && selectedLevel && nextLevel) {
		const getSceneEmoji = (scene: string) => {
			switch (scene) {
				case 'paris': return 'Ben et Julia sont tombe amoureux !'
				case 'venise': return 'Julia partage ses passions avec Ben'
				case 'psg': return 'Ben partage ses passions avec Julia'
				case 'wedding': return '⚽'
				default: return '✨'
			}
		}

		const getSceneGradient = (scene: string) => {
			switch (scene) {
				case 'paris': return 'from-pink-100 via-red-50 to-amber-100'
				case 'venise': return 'from-blue-100 via-cyan-50 to-orange-100'
				case 'psg': return 'from-green-100 via-emerald-50 to-blue-100'
				case 'wedding': return 'from-yellow-100 via-orange-50 to-red-100'
				default: return 'from-purple-100 via-pink-50 to-blue-100'
			}
		}

		return (
			<div className={`min-h-[600px] flex flex-col items-center justify-center p-4`} style={{ minHeight: 600, height: '100vh', maxHeight: 600 }}>
				<div className="max-w-xl w-full mx-auto">
					<motion.div
						initial={{ opacity: 0, scale: 0.8 }}
						animate={{ opacity: 1, scale: 1 }}
						className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-xl text-center"
					>
						{/* Célébration du niveau terminé */}
						<motion.div
							initial={{ opacity: 0, y: -20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ delay: 0.2 }}
							className="mb-8 flex flex-col items-center justify-center"
						>
							<div className="text-7xl mb-2">🎉</div>
							<div className="text-lg text-pink-500 font-semibold mb-2">Bravo !</div>
							<h2 className="text-3xl font-bold text-green-600 mb-2 drop-shadow">
								Niveau {selectedLevel.id} terminé !
							</h2>
							<div className="bg-green-100 text-green-800 py-2 px-6 rounded-full inline-block text-lg font-semibold shadow mb-2">
								Score : {lastCompletedScore} points
							</div>
						</motion.div>

						{/* Présentation du niveau suivant */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ delay: 0.8 }}
							className="mb-8"
						>
							<h2 className="text-2xl font-bold text-purple-600 mb-2">
								Niveau {nextLevel.id}: {nextLevel.name}
							</h2>
							<p className="text-gray-600 mb-4">
								{nextLevel.description}
							</p>
							<div className="flex items-center justify-center gap-2 mb-4">
							</div>
						</motion.div>

						{/* Boutons d'action */}
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ delay: 1.2 }}
							className="flex gap-4 justify-center"
						>
							<motion.button
								onClick={handleContinueToNextLevel}
								className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-lg transition-all duration-300 flex items-center gap-2"
								whileHover={{ scale: 1.05 }}
								whileTap={{ scale: 0.95 }}
							>
								<span>Continuer l'aventure</span>
							</motion.button>
						</motion.div>
					</motion.div>
				</div>
			</div>
		)
	}

// Écran de fin de jeu quand tous les niveaux sont terminés
	if (currentView === 'completed') {
		return (
			<div className="flex flex-col items-center justify-center p-4">
				<div className="max-w-2xl w-full">
					<motion.div
						initial={{ opacity: 0, scale: 0.8 }}
						animate={{ opacity: 1, scale: 1 }}
						className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-xl text-center"
					>
						<div className="text-8xl mb-6">🎉</div>
						<h1 className="text-4xl font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent mb-4">
							Félicitations !
						</h1>
						<p className="text-xl text-gray-600 mb-6">
							Vous avez terminé tous les niveaux de Love Run Adventure !<br/>
							Ben et Julia peuvent enfin se marier ! 💕
						</p>

						<div className="grid grid-cols-2 gap-4 mb-8">
							{levels.map(level => (
								<div key={level.id} className="bg-gray-50 rounded-lg p-4">
									<h3 className="font-semibold text-sm">{level.name}</h3>
									<p className="text-gray-600">Score: {level.bestScore}</p>
								</div>
							))}
						</div>

						<motion.button
							onClick={onBack}
							className="bg-gradient-to-r from-pink-500 to-purple-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-lg transition-all duration-300 flex items-center gap-2 mx-auto mb-4"
							whileHover={{ scale: 1.05 }}
							whileTap={{ scale: 0.95 }}
						>
							Retour au menu principal
						</motion.button>
					</motion.div>
				</div>
			</div>
		)
	}

// Rendu du niveau en cours
	if (currentView === 'playing' && selectedLevel) {
		return renderLevel()
	}

// Fallback (ne devrait jamais arriver)
	return (
		<div className="min-h-fit bg-gradient-to-br from-pink-100 via-purple-50 to-blue-100 flex items-center justify-center">
			<p>Chargement...</p>
		</div>
	)
}

export default LoveRunGame
